import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, ShieldCheck, KeyRound, Layers, RefreshCw } from "lucide-react";
import { sr, auth } from "../../lib/sr";

const CAT_LABEL = {
  global: "Global / Internal", digital: "Digital · DSP", radio: "Radio",
  creative: "Creative", client: "Client", franchise: "Franchise",
};
const CAT_ORDER = ["global", "digital", "radio", "creative", "client", "franchise"];

export default function AdminRoles() {
  const me = auth.getUser();
  const isSuper = me?.role === "super_admin";
  const [data, setData] = useState(null);
  const [migrating, setMigrating] = useState(false);

  useEffect(() => { sr.rbacRoles().then(setData).catch(() => setData({ error: true })); }, []);

  const migrate = async () => {
    if (!window.confirm("Run the role migration? It's safe and idempotent — backfills platform roles + manager assignments.")) return;
    setMigrating(true);
    try {
      const r = await sr.rbacMigrate();
      toast.success(`Migration done · ${r.platform_assignments_created} platform + ${r.manager_assignments_created} manager assignments created`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Migration failed"); }
    finally { setMigrating(false); }
  };

  if (!data) return <div className="sr-empty"><Loader2 className="sr-spin" /></div>;
  if (data.error) return <div className="sr-card"><p className="sr-muted">Could not load roles.</p></div>;

  return (
    <div data-testid="admin-roles-page">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12, marginBottom: 16 }}>
        <div>
          <span className="sr-badge" style={{ marginBottom: 8 }}><ShieldCheck size={12} /> Admin · Roles</span>
          <h1 style={{ fontSize: 30 }}>Roles &amp; permissions</h1>
          <p className="sr-muted" style={{ marginTop: 6 }}>Role templates and the capabilities each grants. Assign roles per client on a client's detail page.</p>
        </div>
        {isSuper && (
          <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={migrate} disabled={migrating} data-testid="run-migration-btn">
            {migrating ? <Loader2 size={15} className="sr-spin" /> : <RefreshCw size={15} />} Run role migration
          </button>
        )}
      </div>

      <div className="sr-card" style={{ marginBottom: 16 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
          <KeyRound size={15} style={{ color: "var(--blue)" }} />
          <strong style={{ color: "var(--navy)" }}>Capability vocabulary</strong>
          <span className="sr-badge sr-badge-gray">{data.capabilities.length}</span>
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {data.capabilities.map((c) => <span key={c} className="sr-badge sr-badge-gray" data-testid={`cap-${c}`}>{c}</span>)}
        </div>
      </div>

      {CAT_ORDER.filter((cat) => data.by_category[cat]).map((cat) => (
        <div key={cat} style={{ marginBottom: 22 }}>
          <h3 style={{ fontSize: 15, color: "var(--navy)", marginBottom: 10, display: "flex", alignItems: "center", gap: 8 }}>
            <Layers size={15} style={{ color: "var(--blue)" }} /> {CAT_LABEL[cat] || cat}
          </h3>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 12 }}>
            {data.by_category[cat].map((t) => (
              <div key={t.slug} className="sr-card" data-testid={`role-${t.slug}`}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                  <strong style={{ color: "var(--navy)" }}>{t.name}</strong>
                  <span className="sr-badge sr-badge-gray">{t.scope}</span>
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 5, marginTop: 10 }}>
                  {t.capabilities.map((c) => <span key={c} className="sr-badge" style={{ fontSize: 11 }}>{c}</span>)}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
